// Théorie musicale de la Veillée (pur, sans Web Audio) : modes, accords, motifs tirés d'un nom, rythmes,
// tirages pseudo-aléatoires reproductibles et le thème principal « La Veillée ».

export type ModeId =
  | "ionien" | "dorien" | "phrygien" | "lydien" | "mixolydien" | "eolien" | "harmonique" | "hijaz" | "tonPar";

/** Intervalles (demi-tons) de chaque mode depuis sa tonique. */
export const MODES: Record<ModeId, readonly number[]> = {
  ionien: [0, 2, 4, 5, 7, 9, 11],
  dorien: [0, 2, 3, 5, 7, 9, 10],
  phrygien: [0, 1, 3, 5, 7, 8, 10],
  lydien: [0, 2, 4, 6, 7, 9, 11],
  mixolydien: [0, 2, 4, 5, 7, 9, 10],
  eolien: [0, 2, 3, 5, 7, 8, 10],
  harmonique: [0, 2, 3, 5, 7, 8, 11],
  hijaz: [0, 1, 4, 5, 7, 8, 10],
  tonPar: [0, 2, 4, 6, 8, 10],
};

/** Du plus lumineux au plus sombre : l'aube éclaire d'un cran, la nuit assombrit d'un cran. */
const BRIGHTNESS: ModeId[] = ["lydien", "ionien", "mixolydien", "dorien", "eolien", "phrygien"];

export function brighter(mode: ModeId, steps = 1): ModeId {
  const i = BRIGHTNESS.indexOf(mode);
  if (i < 0) return mode;
  return BRIGHTNESS[Math.max(0, Math.min(BRIGHTNESS.length - 1, i - steps))];
}

export function darker(mode: ModeId, steps = 1): ModeId {
  return brighter(mode, -steps);
}

/** Enchaînements d'accords (degrés depuis 0) sans accord diminué pour chaque mode. */
export const PROGRESSIONS: Record<ModeId, readonly (readonly number[])[]> = {
  ionien: [[0, 3, 4, 0], [0, 5, 3, 4], [0, 4, 5, 3]],
  dorien: [[0, 6, 0, 3], [0, 3, 6, 0], [0, 2, 3, 6]],
  phrygien: [[0, 1, 0, 6], [0, 1, 6, 0], [0, 5, 1, 0]],
  lydien: [[0, 1, 0, 4], [0, 5, 1, 0], [0, 1, 2, 1]],
  mixolydien: [[0, 6, 3, 0], [0, 4, 6, 0], [0, 3, 6, 3]],
  eolien: [[0, 5, 6, 0], [0, 3, 5, 6], [0, 6, 5, 4]],
  harmonique: [[0, 5, 4, 0], [0, 3, 4, 0], [0, 5, 3, 4]],
  hijaz: [[0, 1, 0, 6], [0, 6, 1, 0], [0, 3, 1, 0]],
  tonPar: [[0, 1, 0, 2], [0, 2, 1, 3]],
};

export function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** Note MIDI d'un degré (négatif ou au-delà de l'octave) du mode, depuis la tonique `root`. */
export function degreeToMidi(root: number, mode: ModeId, degree: number): number {
  const steps = MODES[mode];
  const n = steps.length;
  const octave = Math.floor(degree / n);
  const index = ((degree % n) + n) % n;
  return root + octave * 12 + steps[index];
}

/** Accord de trois sons (fondamentale, tierce, quinte) sur un degré, en degrés du mode. */
export function triad(degree: number): number[] {
  return [degree, degree + 2, degree + 4];
}

/** Le degré de l'accord le plus proche de `near` (pour poser les temps forts sur un son de l'accord). */
export function nearestChordTone(chordDegree: number, near: number, modeSize: number): number {
  let best = near;
  let bestDist = Infinity;
  for (const base of triad(chordDegree)) {
    for (let k = -3; k <= 3; k++) {
      const cand = base + k * modeSize;
      const d = Math.abs(cand - near);
      if (d < bestDist) {
        bestDist = d;
        best = cand;
      }
    }
  }
  return best;
}

// ───────── Hasard reproductible ─────────

export function hashText(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export interface Dice {
  next(): number;
  int(min: number, max: number): number;
  pick<T>(items: readonly T[]): T;
  chance(p: number): boolean;
}

/** mulberry32 : rapide, assez bon pour composer. */
export function dice(seed: number | string): Dice {
  let a = typeof seed === "number" ? seed >>> 0 : hashText(seed);
  const next = (): number => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return {
    next,
    int: (min, max) => min + Math.floor(next() * (max - min + 1)),
    pick: (items) => items[Math.floor(next() * items.length)],
    chance: (p) => next() < p,
  };
}

// ───────── Motifs (leitmotivs) ─────────

/** Un motif : degrés relatifs à son départ et durées en temps. */
export interface Motif { degrees: number[]; beats: number[] }

const MOTIF_RHYTHMS: readonly (readonly number[])[] = [
  [1, 1, 2], [0.5, 0.5, 1, 2], [1, 0.5, 0.5, 2], [1.5, 0.5, 1, 1], [0.5, 0.5, 0.5, 0.5, 2], [2, 1, 1], [1, 1, 1, 1],
];

/**
 * Le motif d'un nom : chaque héros, chaque monde a le sien, toujours le même. Contour en arche ou en vague,
 * pas conjoints surtout, un saut au plus, fin sur un degré stable (0, 2 ou 4).
 */
export function motifFrom(text: string): Motif {
  const r = dice(`motif|${text.normalize("NFC").toLowerCase()}`);
  const beats = [...r.pick(MOTIF_RHYTHMS)];
  const degrees = [0];
  let cur = 0;
  let leapt = false;
  for (let i = 1; i < beats.length; i++) {
    const last = i === beats.length - 1;
    if (last) {
      const stable = [0, 2, 4, -3];
      cur = stable.reduce((best, s) => (Math.abs(s - cur) < Math.abs(best - cur) ? s : best), stable[0]);
      if (cur === degrees[degrees.length - 1]) cur += r.chance(0.5) ? 2 : -1;
    } else if (!leapt && r.chance(0.3)) {
      cur += r.pick([3, 4, -3, 5]);
      leapt = true;
    } else {
      cur += r.pick([1, 1, 2, -1, -1, -2]);
    }
    cur = Math.max(-4, Math.min(7, cur));
    degrees.push(cur);
  }
  return { degrees, beats };
}

// ───────── Rythmes de mesure ─────────

export const BAR_RHYTHMS: Record<number, readonly (readonly number[])[]> = {
  3: [[2, 1], [1, 1, 1], [3], [1, 2], [1.5, 0.5, 1], [1, 0.5, 0.5, 1]],
  4: [[2, 2], [1, 1, 2], [1, 1, 1, 1], [3, 1], [1.5, 0.5, 2], [4], [2, 1, 1], [0.5, 0.5, 1, 2]],
  6: [[3, 3], [2, 1, 3], [2, 1, 2, 1], [3, 2, 1], [6], [1, 1, 1, 3]],
};

export function barRhythm(beatsPerBar: number, r: Dice, density: number): number[] {
  const options = BAR_RHYTHMS[beatsPerBar] ?? BAR_RHYTHMS[4];
  // plus la densité est faible, plus on préfère les rythmes à peu de notes
  const sorted = [...options].sort((a, b) => a.length - b.length);
  const span = Math.max(1, Math.round(sorted.length * (0.35 + density * 0.65)));
  return [...sorted[r.int(0, span - 1)]];
}

// ───────── Le thème « La Veillée » (accueil) ─────────

/** Une note composée : hauteur MIDI (null = silence) et durée en temps. */
export interface ScoreNote { midi: number | null; beats: number }

const D4 = 62;
const n = (offset: number | null, beats: number): ScoreNote => ({ midi: offset === null ? null : D4 + offset, beats });

/**
 * Thème principal, ré dorien à 3/4 : une berceuse de veillée, seize mesures en deux phrases.
 * Décalages en demi-tons depuis ré 4 (la 4 = 7, ré 5 = 12, si 4 = 9 : la sixte dorienne).
 */
export const VEILLEE_THEME: ScoreNote[] = [
  n(7, 2), n(12, 1), n(10, 1), n(7, 1), n(5, 1), n(7, 3),
  n(3, 1), n(5, 1), n(7, 1), n(12, 2), n(14, 1), n(10, 1), n(7, 1), n(9, 1), n(7, 2), n(5, 1), n(7, 3),
  n(12, 2), n(15, 1), n(14, 1), n(12, 1), n(10, 1), n(12, 2), n(7, 1),
  n(9, 1), n(10, 1), n(7, 1), n(5, 2), n(3, 1), n(2, 1), n(3, 1), n(5, 1), n(0, 3), n(null, 3),
];

/** Accords du thème, un par mesure (degrés de ré dorien) : Rém Do Rém Fa Sol Lam Do Rém | Rém Do Rém Sol Do Do Rém Rém. */
export const VEILLEE_CHORDS: number[] = [0, 6, 0, 2, 3, 4, 6, 0, 0, 6, 0, 3, 6, 6, 0, 0];

export const VEILLEE_ROOT = 50; // ré 3
